/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { Uuid } from "../../gen/uuid_util/uuid";

export const protobufPackage = "global_graph";

export interface LoadRequest {
  utime: number;
  requestUuid: Uuid | undefined;
  path: string;
  /**
   * Whether to update the global graph with an associated NCPG (value=False) or to update the graph
   * with the WFF (the default behavior for a global graph built in a single flight).
   */
  noUpdateWithNcpg: boolean;
}

export interface LoadResponse {
  utime: number;
  requestUuid: Uuid | undefined;
  success: boolean;
}

export interface SaveRequest {
  utime: number;
  requestUuid: Uuid | undefined;
  saveType: SaveRequest_SaveType;
}

export enum SaveRequest_SaveType {
  /** IMMEDIATE - Immediately save the global graph. Default option for backwards compatibility. */
  IMMEDIATE = 0,
  /**
   * WHEN_LANDING - Regardless of when the save request is made, the global graph will be saved whenever the
   * policy transitions into landing. A response is published for each save. If the request is
   * received while already landing, then it will immediately trigger a save.
   * NOTE: The global graph will be saved multiple times if there are multiple transitions into
   * landing; a DeferredSaveEvent will be published in each case.
   */
  WHEN_LANDING = 1,
  UNRECOGNIZED = -1,
}

export function saveRequest_SaveTypeFromJSON(object: any): SaveRequest_SaveType {
  switch (object) {
    case 0:
    case "IMMEDIATE":
      return SaveRequest_SaveType.IMMEDIATE;
    case 1:
    case "WHEN_LANDING":
      return SaveRequest_SaveType.WHEN_LANDING;
    case -1:
    case "UNRECOGNIZED":
    default:
      return SaveRequest_SaveType.UNRECOGNIZED;
  }
}

export function saveRequest_SaveTypeToJSON(object: SaveRequest_SaveType): string {
  switch (object) {
    case SaveRequest_SaveType.IMMEDIATE:
      return "IMMEDIATE";
    case SaveRequest_SaveType.WHEN_LANDING:
      return "WHEN_LANDING";
    case SaveRequest_SaveType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export interface SaveResponse {
  utime: number;
  requestUuid: Uuid | undefined;
  immediateSaveResult?: SaveResponse_ImmediateSaveResult | undefined;
  deferredSaveResult?: SaveResponse_DeferredSaveResult | undefined;
}

export interface SaveResponse_ImmediateSaveResult {
  success: boolean;
}

export interface SaveResponse_DeferredSaveResult {
  immediateSaveOutcome: SaveResponse_DeferredSaveResult_ImmediateSaveOutcome;
}

/**
 * The global graph may be immediately saved if the vehicle is already landing. Only if the
 * immediate save was attempted and failed will the deferred response not be enqueued.
 */
export enum SaveResponse_DeferredSaveResult_ImmediateSaveOutcome {
  IMMEDIATE_SAVE_NOT_ATTEMPTED = 0,
  IMMEDIATE_SAVE_SUCCEEDED = 1,
  IMMEDIATE_SAVE_FAILED = 2,
  UNRECOGNIZED = -1,
}

export function saveResponse_DeferredSaveResult_ImmediateSaveOutcomeFromJSON(
  object: any,
): SaveResponse_DeferredSaveResult_ImmediateSaveOutcome {
  switch (object) {
    case 0:
    case "IMMEDIATE_SAVE_NOT_ATTEMPTED":
      return SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_NOT_ATTEMPTED;
    case 1:
    case "IMMEDIATE_SAVE_SUCCEEDED":
      return SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_SUCCEEDED;
    case 2:
    case "IMMEDIATE_SAVE_FAILED":
      return SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_FAILED;
    case -1:
    case "UNRECOGNIZED":
    default:
      return SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.UNRECOGNIZED;
  }
}

export function saveResponse_DeferredSaveResult_ImmediateSaveOutcomeToJSON(
  object: SaveResponse_DeferredSaveResult_ImmediateSaveOutcome,
): string {
  switch (object) {
    case SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_NOT_ATTEMPTED:
      return "IMMEDIATE_SAVE_NOT_ATTEMPTED";
    case SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_SUCCEEDED:
      return "IMMEDIATE_SAVE_SUCCEEDED";
    case SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.IMMEDIATE_SAVE_FAILED:
      return "IMMEDIATE_SAVE_FAILED";
    case SaveResponse_DeferredSaveResult_ImmediateSaveOutcome.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Status message for a deferred save occurs */
export interface DeferredSaveEvent {
  utime: number;
  /** Identifies the request that requested the deferred saving */
  requestUuid: Uuid | undefined;
  success: boolean;
}

function createBaseLoadRequest(): LoadRequest {
  return { utime: 0, requestUuid: undefined, path: "", noUpdateWithNcpg: false };
}

export const LoadRequest = {
  encode(message: LoadRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.requestUuid !== undefined) {
      Uuid.encode(message.requestUuid, writer.uint32(18).fork()).ldelim();
    }
    if (message.path !== "") {
      writer.uint32(26).string(message.path);
    }
    if (message.noUpdateWithNcpg === true) {
      writer.uint32(32).bool(message.noUpdateWithNcpg);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoadRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoadRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.requestUuid = Uuid.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.path = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.noUpdateWithNcpg = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoadRequest {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      requestUuid: isSet(object.requestUuid) ? Uuid.fromJSON(object.requestUuid) : undefined,
      path: isSet(object.path) ? globalThis.String(object.path) : "",
      noUpdateWithNcpg: isSet(object.noUpdateWithNcpg) ? globalThis.Boolean(object.noUpdateWithNcpg) : false,
    };
  },

  toJSON(message: LoadRequest): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.requestUuid !== undefined) {
      obj.requestUuid = Uuid.toJSON(message.requestUuid);
    }
    if (message.path !== "") {
      obj.path = message.path;
    }
    if (message.noUpdateWithNcpg === true) {
      obj.noUpdateWithNcpg = message.noUpdateWithNcpg;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoadRequest>, I>>(base?: I): LoadRequest {
    return LoadRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoadRequest>, I>>(object: I): LoadRequest {
    const message = createBaseLoadRequest();
    message.utime = object.utime ?? 0;
    message.requestUuid = (object.requestUuid !== undefined && object.requestUuid !== null)
      ? Uuid.fromPartial(object.requestUuid)
      : undefined;
    message.path = object.path ?? "";
    message.noUpdateWithNcpg = object.noUpdateWithNcpg ?? false;
    return message;
  },
};

function createBaseLoadResponse(): LoadResponse {
  return { utime: 0, requestUuid: undefined, success: false };
}

export const LoadResponse = {
  encode(message: LoadResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.requestUuid !== undefined) {
      Uuid.encode(message.requestUuid, writer.uint32(18).fork()).ldelim();
    }
    if (message.success === true) {
      writer.uint32(24).bool(message.success);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): LoadResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseLoadResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.requestUuid = Uuid.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.success = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): LoadResponse {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      requestUuid: isSet(object.requestUuid) ? Uuid.fromJSON(object.requestUuid) : undefined,
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
    };
  },

  toJSON(message: LoadResponse): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.requestUuid !== undefined) {
      obj.requestUuid = Uuid.toJSON(message.requestUuid);
    }
    if (message.success === true) {
      obj.success = message.success;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<LoadResponse>, I>>(base?: I): LoadResponse {
    return LoadResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<LoadResponse>, I>>(object: I): LoadResponse {
    const message = createBaseLoadResponse();
    message.utime = object.utime ?? 0;
    message.requestUuid = (object.requestUuid !== undefined && object.requestUuid !== null)
      ? Uuid.fromPartial(object.requestUuid)
      : undefined;
    message.success = object.success ?? false;
    return message;
  },
};

function createBaseSaveRequest(): SaveRequest {
  return { utime: 0, requestUuid: undefined, saveType: 0 };
}

export const SaveRequest = {
  encode(message: SaveRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.requestUuid !== undefined) {
      Uuid.encode(message.requestUuid, writer.uint32(18).fork()).ldelim();
    }
    if (message.saveType !== 0) {
      writer.uint32(24).int32(message.saveType);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SaveRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSaveRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.requestUuid = Uuid.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.saveType = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SaveRequest {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      requestUuid: isSet(object.requestUuid) ? Uuid.fromJSON(object.requestUuid) : undefined,
      saveType: isSet(object.saveType) ? saveRequest_SaveTypeFromJSON(object.saveType) : 0,
    };
  },

  toJSON(message: SaveRequest): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.requestUuid !== undefined) {
      obj.requestUuid = Uuid.toJSON(message.requestUuid);
    }
    if (message.saveType !== 0) {
      obj.saveType = saveRequest_SaveTypeToJSON(message.saveType);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SaveRequest>, I>>(base?: I): SaveRequest {
    return SaveRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SaveRequest>, I>>(object: I): SaveRequest {
    const message = createBaseSaveRequest();
    message.utime = object.utime ?? 0;
    message.requestUuid = (object.requestUuid !== undefined && object.requestUuid !== null)
      ? Uuid.fromPartial(object.requestUuid)
      : undefined;
    message.saveType = object.saveType ?? 0;
    return message;
  },
};

function createBaseSaveResponse(): SaveResponse {
  return { utime: 0, requestUuid: undefined, immediateSaveResult: undefined, deferredSaveResult: undefined };
}

export const SaveResponse = {
  encode(message: SaveResponse, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.requestUuid !== undefined) {
      Uuid.encode(message.requestUuid, writer.uint32(18).fork()).ldelim();
    }
    if (message.immediateSaveResult !== undefined) {
      SaveResponse_ImmediateSaveResult.encode(message.immediateSaveResult, writer.uint32(26).fork()).ldelim();
    }
    if (message.deferredSaveResult !== undefined) {
      SaveResponse_DeferredSaveResult.encode(message.deferredSaveResult, writer.uint32(34).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SaveResponse {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSaveResponse();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.requestUuid = Uuid.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.immediateSaveResult = SaveResponse_ImmediateSaveResult.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.deferredSaveResult = SaveResponse_DeferredSaveResult.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SaveResponse {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      requestUuid: isSet(object.requestUuid) ? Uuid.fromJSON(object.requestUuid) : undefined,
      immediateSaveResult: isSet(object.immediateSaveResult)
        ? SaveResponse_ImmediateSaveResult.fromJSON(object.immediateSaveResult)
        : undefined,
      deferredSaveResult: isSet(object.deferredSaveResult)
        ? SaveResponse_DeferredSaveResult.fromJSON(object.deferredSaveResult)
        : undefined,
    };
  },

  toJSON(message: SaveResponse): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.requestUuid !== undefined) {
      obj.requestUuid = Uuid.toJSON(message.requestUuid);
    }
    if (message.immediateSaveResult !== undefined) {
      obj.immediateSaveResult = SaveResponse_ImmediateSaveResult.toJSON(message.immediateSaveResult);
    }
    if (message.deferredSaveResult !== undefined) {
      obj.deferredSaveResult = SaveResponse_DeferredSaveResult.toJSON(message.deferredSaveResult);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SaveResponse>, I>>(base?: I): SaveResponse {
    return SaveResponse.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SaveResponse>, I>>(object: I): SaveResponse {
    const message = createBaseSaveResponse();
    message.utime = object.utime ?? 0;
    message.requestUuid = (object.requestUuid !== undefined && object.requestUuid !== null)
      ? Uuid.fromPartial(object.requestUuid)
      : undefined;
    message.immediateSaveResult = (object.immediateSaveResult !== undefined && object.immediateSaveResult !== null)
      ? SaveResponse_ImmediateSaveResult.fromPartial(object.immediateSaveResult)
      : undefined;
    message.deferredSaveResult = (object.deferredSaveResult !== undefined && object.deferredSaveResult !== null)
      ? SaveResponse_DeferredSaveResult.fromPartial(object.deferredSaveResult)
      : undefined;
    return message;
  },
};

function createBaseSaveResponse_ImmediateSaveResult(): SaveResponse_ImmediateSaveResult {
  return { success: false };
}

export const SaveResponse_ImmediateSaveResult = {
  encode(message: SaveResponse_ImmediateSaveResult, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.success === true) {
      writer.uint32(8).bool(message.success);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SaveResponse_ImmediateSaveResult {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSaveResponse_ImmediateSaveResult();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.success = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SaveResponse_ImmediateSaveResult {
    return { success: isSet(object.success) ? globalThis.Boolean(object.success) : false };
  },

  toJSON(message: SaveResponse_ImmediateSaveResult): unknown {
    const obj: any = {};
    if (message.success === true) {
      obj.success = message.success;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SaveResponse_ImmediateSaveResult>, I>>(
    base?: I,
  ): SaveResponse_ImmediateSaveResult {
    return SaveResponse_ImmediateSaveResult.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SaveResponse_ImmediateSaveResult>, I>>(
    object: I,
  ): SaveResponse_ImmediateSaveResult {
    const message = createBaseSaveResponse_ImmediateSaveResult();
    message.success = object.success ?? false;
    return message;
  },
};

function createBaseSaveResponse_DeferredSaveResult(): SaveResponse_DeferredSaveResult {
  return { immediateSaveOutcome: 0 };
}

export const SaveResponse_DeferredSaveResult = {
  encode(message: SaveResponse_DeferredSaveResult, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.immediateSaveOutcome !== 0) {
      writer.uint32(8).int32(message.immediateSaveOutcome);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): SaveResponse_DeferredSaveResult {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseSaveResponse_DeferredSaveResult();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.immediateSaveOutcome = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): SaveResponse_DeferredSaveResult {
    return {
      immediateSaveOutcome: isSet(object.immediateSaveOutcome)
        ? saveResponse_DeferredSaveResult_ImmediateSaveOutcomeFromJSON(object.immediateSaveOutcome)
        : 0,
    };
  },

  toJSON(message: SaveResponse_DeferredSaveResult): unknown {
    const obj: any = {};
    if (message.immediateSaveOutcome !== 0) {
      obj.immediateSaveOutcome = saveResponse_DeferredSaveResult_ImmediateSaveOutcomeToJSON(
        message.immediateSaveOutcome,
      );
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<SaveResponse_DeferredSaveResult>, I>>(base?: I): SaveResponse_DeferredSaveResult {
    return SaveResponse_DeferredSaveResult.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<SaveResponse_DeferredSaveResult>, I>>(
    object: I,
  ): SaveResponse_DeferredSaveResult {
    const message = createBaseSaveResponse_DeferredSaveResult();
    message.immediateSaveOutcome = object.immediateSaveOutcome ?? 0;
    return message;
  },
};

function createBaseDeferredSaveEvent(): DeferredSaveEvent {
  return { utime: 0, requestUuid: undefined, success: false };
}

export const DeferredSaveEvent = {
  encode(message: DeferredSaveEvent, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.requestUuid !== undefined) {
      Uuid.encode(message.requestUuid, writer.uint32(18).fork()).ldelim();
    }
    if (message.success === true) {
      writer.uint32(24).bool(message.success);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): DeferredSaveEvent {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDeferredSaveEvent();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.requestUuid = Uuid.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.success = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): DeferredSaveEvent {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      requestUuid: isSet(object.requestUuid) ? Uuid.fromJSON(object.requestUuid) : undefined,
      success: isSet(object.success) ? globalThis.Boolean(object.success) : false,
    };
  },

  toJSON(message: DeferredSaveEvent): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.requestUuid !== undefined) {
      obj.requestUuid = Uuid.toJSON(message.requestUuid);
    }
    if (message.success === true) {
      obj.success = message.success;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<DeferredSaveEvent>, I>>(base?: I): DeferredSaveEvent {
    return DeferredSaveEvent.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<DeferredSaveEvent>, I>>(object: I): DeferredSaveEvent {
    const message = createBaseDeferredSaveEvent();
    message.utime = object.utime ?? 0;
    message.requestUuid = (object.requestUuid !== undefined && object.requestUuid !== null)
      ? Uuid.fromPartial(object.requestUuid)
      : undefined;
    message.success = object.success ?? false;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
